import { FC, useCallback, useMemo } from 'react';

import { Loader } from 'components';
import { TireDamage, TireDamageType, TireInspection } from 'pages/new-transaction/tires';
import { useEditTransaction } from './edit-transaction.context';

const tireDamages: TireDamageType[] = Object.values(TireDamage);

const isTireDamage = (value: any): value is TireDamageType => tireDamages.includes(value);

const TireCell: FC<Pick<TireInspection, 'axle' | 'centrality' | 'side'>> = ({ axle, centrality, side }) => {
  const { transaction, setTransaction } = useEditTransaction();

  const { tire, index }: { tire?: TireInspection; index?: number } = useMemo(() => {
    const tires: TireInspection[] = Object.assign([], transaction?.tires);
    const index = tires.findIndex(t => t.axle === axle && t.centrality === centrality && t.side === side) ?? -1;
    return { index, tire: tires[index] };
  }, [axle, centrality, side, transaction?.tires]);

  const setTire = useCallback(
    (p: Partial<TireInspection>) => {
      setTransaction(t => {
        if (t && index >= 0) {
          const tires: TireInspection[] = Object.assign([], t?.tires);
          tires[index] = { ...tire, ...p } as TireInspection;
          return { ...t, tires };
        } else return t;
      });
    },
    [index, tire, setTransaction]
  );

  return (
    <div className="grid-1 mt-2 tire-row">
      <div className="d-flex g-1">
        <input
          className="tire-measurement"
          type="text"
          inputMode="numeric"
          value={tire?.measurement0 ?? ''}
          onChange={e => setTire({ measurement0: isNaN(Number(e.target.value)) ? undefined : Number(e.target.value) })}
        />
        {tire?.damageType === 'SLID FLAT' && (
          <>
            <input
              className="tire-measurement"
              type="text"
              inputMode="numeric"
              value={tire.measurement1 ?? ''}
              onChange={e =>
                setTire({ measurement1: isNaN(Number(e.target.value)) ? undefined : Number(e.target.value) })
              }
            />
            <input
              className="tire-measurement"
              type="text"
              inputMode="numeric"
              value={tire.measurement2 ?? ''}
              onChange={e =>
                setTire({ measurement2: isNaN(Number(e.target.value)) ? undefined : Number(e.target.value) })
              }
            />
          </>
        )}
        <div className="flex-fill select">
          <label hidden={!!tire?.damageType}>DAMAGE</label>
          <select
            className="w-100"
            value={tire?.damageType ?? ''}
            onChange={e => setTire({ damageType: isTireDamage(e.target.value) ? e.target.value : undefined })}
          >
            <option value="">{!tire?.damageType ? '' : 'NONE'}</option>
            {tireDamages.map(value => (
              <option key={value} value={value} children={value} />
            ))}
          </select>
        </div>
      </div>
      <input
        type="text"
        value={tire?.notes ?? ''}
        onChange={e => setTire({ notes: e.target.value })}
        placeholder="NOTES"
      />
    </div>
  );
};

const TireColumn: FC<Pick<TireInspection, 'centrality' | 'side'>> = props => {
  const { transaction } = useEditTransaction();

  const isTriaxle: boolean = useMemo(() => {
    const units = Object.assign([], transaction?.inbound ? transaction.gateInUnits : transaction?.gateOutUnits);
    return units.some(({ sizeType }) => sizeType === '20TX' || sizeType === '40TX');
  }, [transaction?.inbound, transaction?.gateInUnits, transaction?.gateOutUnits]);

  return (
    <div className="tire-column">
      <h6 className="text-primary mt-3 mb-3">
        {props.centrality === 'INNER' ? 'INSIDE' : 'OUTSIDE'} {props.side}
      </h6>
      <div className="grid-1">
        <div>
          <h6 className="text-info-dark">FRONT</h6>
          <TireCell axle="FRONT" {...props} />
        </div>
        {isTriaxle && (
          <div>
            <h6 className="text-info-dark">CENTER</h6>
            <TireCell axle="CENTER" {...props} />
          </div>
        )}
        <div>
          <h6 className="text-info-dark">REAR</h6>
          <TireCell axle="REAR" {...props} />
        </div>
      </div>
    </div>
  );
};

export const EditTires: FC = () => {
  const { transaction } = useEditTransaction();

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>TIRE SUMMARY</h4>
      </div>
      <div className="card-body">
        <Loader loading={transaction === undefined}>
          <div className="grid-4 p-2">
            <TireColumn centrality="OUTER" side="LEFT" />
            <TireColumn centrality="INNER" side="LEFT" />
            <TireColumn centrality="INNER" side="RIGHT" />
            <TireColumn centrality="OUTER" side="RIGHT" />
          </div>
        </Loader>
      </div>
    </div>
  );
};

export default EditTires;
